import { AfterRenderRef, Component, Injector, OnDestroy, afterNextRender, computed, inject, input, output, signal } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import moment from 'moment';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { AppConfig } from '../shared/app.config';
import {
  buildSelectedSubscriber,
  isAddFlowHandoffEvent,
  normalizeFoundMemberForPrefill,
  resolveAddFlowHandoffFoundMember,
  resolveMemberId,
  resolveSubscriberEnrollmentId
} from '../shared/member-feed.util';
import { clearVisibilityTimer, createConfirmSsnForm, isCopyShortcut, restartVisibilityTimer } from '../shared/utils/ssn-form.util';
import { IClientPlanDetails } from '../store/massActions/state';
import { firstValueFrom, of } from 'rxjs';
import { startWith, switchMap, take } from 'rxjs/operators';
import { PseudoSsnApiService } from '../store/pseudoSsn/api.service';

type MemberIdEntryMode = 'manual' | 'system' | 'both';

type FamilyEnrollDefaults = {
  adminPlan: string;
  asOf: string;
  clientId: string;
  eligibilityEffectiveDate: string;
  eligibilityStatus: string;
  eligibilityStatusReason: string;
  memberBenefitType: string;
  memberId: string;
  planAcronym: string;
  pseudoSsnInd?: boolean;
  qualifierType: string;
  receivedDate: string;
  subClientId: string;
  subscriberEnrollmentID: string;
  foundMember?: unknown;
};

type FamilyEnrollmentLuxState = {
  memberDetails?: Record<string, {
    findMemberState?: {
      foundMember?: unknown;
    };
  } | undefined>;
};

@Component({
  selector: 'app-family-enrollment-page',
  templateUrl: './family-enrollment.component.html',
  styleUrls: ['./family-enrollment.component.scss'],
  standalone: false
})
export class FamilyEnrollmentComponent implements OnDestroy {
  private readonly injector = inject(Injector);
  private readonly lux = inject<Lux<FamilyEnrollmentLuxState>>(Lux);
  familyEnrollFeatures: string[] = [];
  readonly region = this.config.region;
  readonly selectedAdminPlan = input('');
  readonly familyEnrollDefaults = input<FamilyEnrollDefaults | null>(null);
  readonly cachedFoundMember = toSignal(
    this.lux.get((state: FamilyEnrollmentLuxState) => state.memberDetails?.['BMT']?.findMemberState?.foundMember, 'family-enrollment-found-member'),
    { initialValue: null as unknown }
  );
  readonly clientDetails = input<IClientPlanDetails | null>(null);
  readonly addMemberForm = signal<UntypedFormGroup | null>(null);
  setDefaultsForm: UntypedFormGroup;
  readonly changeType = computed<'text' | 'password'>(() => {
    const ssnValue = this.ssnValue() || '';
    return ssnValue.length >= 9 ? 'password' : 'text';
  });
  private readonly ssnValue = toSignal(
    toObservable(this.addMemberForm, { injector: this.injector }).pipe(
      switchMap(addMemberForm => {
        const control = addMemberForm?.get('ssn');
        return control
          ? control.valueChanges.pipe(startWith(control.value || ''))
          : of('');
      })
    ),
    { initialValue: '', injector: this.injector }
  );
  readonly showCopyError = signal(false);
  readonly displaySsnForm = signal(false);
  readonly findMemberFeed = signal<{ planAcronym: string; clientId: string; subClientId: string; memberId: any; pseudoSsnInd: boolean; adminPlan: string } | null>(null);
  readonly setMemberStore = output<any>();
  readonly eligStatusReasons = input<Array<{ value: string; label: string }>>([]);
  readonly qualifierTypes = input<Array<{ value: string; label: string }>>([]);
  readonly memberBenefitTypes = input<Array<{ value: string; label: string }>>([]);
  private copyErrorTimer: number | null = null;
  private openAddAnotherDialogRef: AfterRenderRef | null = null;
  readonly memberIdEntryMode = signal<MemberIdEntryMode>('manual');
  readonly isGeneratingPseudoSsn = signal(false);
  readonly pseudoSsnError = signal('');


  constructor(
    private readonly config: AppConfig,
    private readonly route: Router,
    private readonly fb: UntypedFormBuilder,
    private readonly pseudoSsnApi: PseudoSsnApiService,
  ) {}

  cancelFamilyEnrollment(): void {
    this.setMemberStore.emit({
      addMemberFeed: {
        planAcronym: '',
        clientId: '',
        subClientId: '',
        asOf: '',
        memberId: '',
        subscriberEnrollmentID: '',
        eligibilityEffectiveDate: '',
        receivedDate: '',
        qualifierType: '',
        memberBenefitType: '',
        eligibilityStatusReason: '',
        eligibilityStatus: ''
      },
      event: 'elig-member-search'
    });
    this.route.navigate(['/eligibility/member']);
  }

  wasEnrollmentSuccess(outputValue: string): void {
    if (outputValue === 'reset') {
      this.route.navigate(['/eligibility/family-enrollment']);
      return;
    }

    if (outputValue === 'close') {
      this.cancelFamilyEnrollment();
      return;
    }

    if (outputValue === 'add') {
      this.openAddAnotherDialog();
    }
  }

  private openAddAnotherDialog(): void {
    this.openAddAnotherDialogRef?.destroy();
    this.openAddAnotherDialogRef = afterNextRender(() => {
      this.openAddAnotherDialogRef = null;
      this.findMemberFeed.set(null);
      this.setMemberIdEntryMode(this.clientDetails()?.client?.subClient);
      this.setFamilyEnrollDefaultsForm();
      this.setConfirmSsnForm();
      this.displaySsnForm.set(true);
    }, { injector: this.injector });
  }

  setMemberIdEntryMode(selectedSubClient: any): void {
    const personIdentifierConfigs = Array.isArray(selectedSubClient?.personIdentifierConfigs)
      ? selectedSubClient.personIdentifierConfigs
      : [];
    const ssnConfig = personIdentifierConfigs.find(config => config?.type === 'SSN');
    const sourceCodes = Array.isArray(ssnConfig?.sources)
      ? ssnConfig.sources.map(source => source?.sourceType?.code)
      : [];
    const hasGroupAssignedSource = sourceCodes.includes('GROUP ASSIGNED') || sourceCodes.includes('GROUP_ASSIGNED');
    const hasSystemAssignedSource = sourceCodes.includes('SYSTEM ASSIGNED') || sourceCodes.includes('SYSTEM_ASSIGNED');

    if (hasGroupAssignedSource && hasSystemAssignedSource) {
      this.memberIdEntryMode.set('both');
    } else if (hasSystemAssignedSource) {
      this.memberIdEntryMode.set('system');
    } else {
      this.memberIdEntryMode.set('manual');
    }
    this.pseudoSsnError.set('');
  }

  setConfirmSsnForm(): void {
    this.addMemberForm.set(createConfirmSsnForm(this.fb));
  }

  setFamilyEnrollDefaultsForm(): void {
    const defaults = this.familyEnrollDefaults();
    if (!defaults) {
      return;
    }

    this.setDefaultsForm = this.fb.group({
      receivedDate: [{ value: moment(defaults.receivedDate).format('MM/DD/YYYY'), disabled: true }],
      eligibilityEffectiveDate: [moment(defaults.eligibilityEffectiveDate).format('MM/DD/YYYY'), Validators.required],
      eligibilityStatus: [defaults.eligibilityStatus || 'active', Validators.required],
      eligibilityStatusReason: [defaults.eligibilityStatusReason, Validators.required],
      qualifierType: [defaults.qualifierType],
      memberBenefitType: [defaults.memberBenefitType]
    });
  }

  onKeyPress($event: KeyboardEvent): void {
    if (isCopyShortcut($event)) {
      $event.preventDefault();
      this.copyErrorTimer = restartVisibilityTimer(this.showCopyError, this.copyErrorTimer, () => {
        this.copyErrorTimer = null;
      });
    }
  }

  triggerFindMember(memberId = this.addMemberForm()?.value?.confirmSsn, pseudoSsnInd = false): void {
    const defaults = this.familyEnrollDefaults();
    const addMemberForm = this.addMemberForm();
    if (!defaults || (!pseudoSsnInd && !addMemberForm?.valid)) {
      return;
    }

    this.findMemberFeed.set({
      planAcronym: defaults.planAcronym,
      clientId: defaults.clientId,
      subClientId: defaults.subClientId,
      memberId,
      pseudoSsnInd,
      adminPlan: this.selectedAdminPlan()
    });
  }

  async generatePseudoSsn(): Promise<void> {
    if (this.isGeneratingPseudoSsn()) {
      return;
    }

    this.isGeneratingPseudoSsn.set(true);
    this.pseudoSsnError.set('');
    try {
      const memberId = await firstValueFrom(this.pseudoSsnApi.generate().pipe(take(1)));
      if (memberId) {
        this.triggerFindMember(memberId, true);
      } else {
        this.pseudoSsnError.set('The pseudo SSN generation API did not return a member ID.');
      }
    } catch {
      this.pseudoSsnError.set('Unable to generate a pseudo SSN. Please try again.');
    } finally {
      this.isGeneratingPseudoSsn.set(false);
    }
  }

  continueWithGeneratedPseudoSsn(): void {
    if (!this.setDefaultsForm?.valid) {
      this.setDefaultsForm?.markAllAsTouched();
      return;
    }

    this.generatePseudoSsn();
  }

  foundMember(event: any): void {
    if (event === 'cancel') {
      this.cancelFamilyEnrollment();
      return;
    }

    this.displaySsnForm.set(false);
    this.setAddMemberFeed(event);
  }

  setAddMemberFeed(event: any): void {
    const defaults = this.familyEnrollDefaults();
    const addMemberForm = this.addMemberForm();
    if (!defaults || !this.setDefaultsForm) {
      return;
    }

    const isAddFlowEvent = isAddFlowHandoffEvent(event);
    let rawFoundMember = event;
    if (isAddFlowEvent) {
      const addFlowFallbackFoundMember = typeof event === 'string'
        ? this.cachedFoundMember()
        : null;
      rawFoundMember = resolveAddFlowHandoffFoundMember(event) ?? addFlowFallbackFoundMember;
    }
    const normalizedFoundMember = normalizeFoundMemberForPrefill(rawFoundMember);
    const resolvedMemberId = isAddFlowEvent
      ? (this.findMemberFeed()?.memberId ?? addMemberForm?.value?.confirmSsn)
      : (resolveMemberId(normalizedFoundMember) ?? addMemberForm?.value?.confirmSsn);
    const resolvedSubscriberEnrollmentId = resolveSubscriberEnrollmentId(normalizedFoundMember) ?? defaults.subscriberEnrollmentID ?? '';
    const defaultsFormValue = this.setDefaultsForm.getRawValue();

    this.setMemberStore.emit({
      addMemberFeed: {
        planAcronym: defaults.planAcronym,
        clientId: defaults.clientId,
        subClientId: defaults.subClientId,
        asOf: defaults.asOf,
        memberId: resolvedMemberId,
        pseudoSsnInd: this.findMemberFeed()?.pseudoSsnInd ?? defaults.pseudoSsnInd === true,
        subscriberEnrollmentID: resolvedSubscriberEnrollmentId,
        foundMember: normalizedFoundMember,
        eligibilityEffectiveDate: defaultsFormValue.eligibilityEffectiveDate
          ? moment(defaultsFormValue.eligibilityEffectiveDate).format('YYYY-MM-DD')
          : defaultsFormValue.eligibilityEffectiveDate,
        receivedDate: defaultsFormValue.receivedDate
          ? moment(defaultsFormValue.receivedDate).format('YYYY-MM-DD')
          : defaultsFormValue.receivedDate,
        qualifierType: defaultsFormValue.qualifierType,
        memberBenefitType: defaultsFormValue.memberBenefitType,
        eligibilityStatusReason: defaultsFormValue.eligibilityStatusReason,
        eligibilityStatus: defaultsFormValue.eligibilityStatus
      },
      event: isAddFlowEvent ? 'add flow' : event,
      ...(normalizedFoundMember && {
        selectedSubscriber: buildSelectedSubscriber(normalizedFoundMember, resolvedSubscriberEnrollmentId)
      })
    });
  }

  ngOnDestroy(): void {
    this.openAddAnotherDialogRef?.destroy();
    this.openAddAnotherDialogRef = null;
    this.clearCopyErrorTimer();
  }

  private clearCopyErrorTimer(): void {
    this.copyErrorTimer = clearVisibilityTimer(this.copyErrorTimer);
  }

}
